import { useState, useCallback, useEffect, useRef } from 'react';
import axios from '../components/AxiosInstance.jsx';
import useSSE from './useSSE';

const useMultiGame = (gameId) => {
  const [multiState, setMultiState] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [turnResolved, setTurnResolved] = useState(false);
  const prevTurnRef = useRef(null);

  // --- Fetch séparés : state léger vs grid lourd ---
  const fetchStateOnly = useCallback(async () => {
    if (!gameId) return;
    try {
      const res = await axios.get(`/api/multi/game_state/${gameId}`);
      const state = res.data.game_state;

      const newTurn = state.multi?.current_turn;
      if (prevTurnRef.current !== null && newTurn > prevTurnRef.current) {
        setTurnResolved(true);
      } else {
        setTurnResolved(false);
      }
      prevTurnRef.current = newTurn;

      setGameState(state);
      setMultiState(state.multi);
      return state;
    } catch (err) {
      console.error('Error fetching multi game state:', err);
    }
  }, [gameId]);

  const fetchGrid = useCallback(async () => {
    if (!gameId) return;
    try {
      const res = await axios.get(`/api/multi/grid/${gameId}`);
      setGridData(res.data);
    } catch (err) {
      console.error('Error fetching multi grid:', err);
    }
  }, [gameId]);

  const fetchGameState = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    try {
      await Promise.all([fetchStateOnly(), fetchGrid()]);
    } finally {
      setLoading(false);
    }
  }, [gameId, fetchStateOnly, fetchGrid]);

  // --- SSE handlers : mises à jour temps réel ---
  const sseHandlers = {
    turn_submitted: (data) => {
      setMultiState(prev => prev ? {
        ...prev,
        players: prev.players.map(p =>
          p.username === data.username ? { ...p, has_submitted: true } : p
        ),
      } : prev);
    },
    turn_resolving: () => {
      setMultiState(prev => prev ? { ...prev, turn_phase: 'resolving' } : prev);
    },
    turn_resolved: (data) => {
      setTurnResolved(true);
      // Tour résolu : recharger state + grid (la grid a changé)
      fetchGameState();
    },
    player_eliminated: (data) => {
      setMultiState(prev => prev ? {
        ...prev,
        players: prev.players.map(p =>
          p.username === data.username ? { ...p, is_eliminated: true } : p
        ),
      } : prev);
    },
    player_disconnected: (data) => {
      setMultiState(prev => prev ? {
        ...prev,
        players: prev.players.map(p =>
          p.username === data.username ? { ...p, is_connected: false } : p
        ),
      } : prev);
    },
    player_reconnected: (data) => {
      setMultiState(prev => prev ? {
        ...prev,
        players: prev.players.map(p =>
          p.username === data.username ? { ...p, is_connected: true } : p
        ),
      } : prev);
    },
    timer_warning: (data) => {
      setMultiState(prev => prev ? { ...prev, timer_warning: data.seconds_remaining } : prev);
    },
    timer_expired: (data) => {
      setMultiState(prev => prev ? {
        ...prev,
        players: prev.players.map(p =>
          p.username === data.username ? { ...p, has_submitted: true } : p
        ),
      } : prev);
    },
    game_over: (data) => {
      setMultiState(prev => prev ? {
        ...prev,
        status: 'finished',
        rankings: data.rankings,
        winner: data.winner,
      } : prev);
    },
    vote_end_initiated: (data) => {
      setMultiState(prev => prev ? { ...prev, active_vote: data } : prev);
    },
    vote_end_updated: (data) => {
      setMultiState(prev => prev ? { ...prev, active_vote: data } : prev);
    },
    vote_end_cancelled: () => {
      setMultiState(prev => prev ? { ...prev, active_vote: null } : prev);
    },
  };

  const { isConnected } = useSSE(gameId, sseHandlers);

  // Fetch initial complet (state + grid)
  useEffect(() => {
    fetchGameState();
  }, [fetchGameState]);

  // Polling fallback : 30s si SSE connecté, 5s sinon
  useEffect(() => {
    if (!gameId || multiState?.status === 'finished') return;
    const interval = isConnected ? 30000 : 5000;
    // Quand SSE est connecté, le polling ne fetch que le state (pas la grid)
    const pollFn = isConnected ? fetchStateOnly : fetchGameState;
    const timer = setInterval(pollFn, interval);
    return () => clearInterval(timer);
  }, [gameId, multiState?.status, isConnected, fetchStateOnly, fetchGameState]);

  const voteEnd = useCallback(async (action) => {
    try {
      const res = await axios.post('/api/multi/vote_end', {
        game_id: parseInt(gameId),
        action,
      });
      return res.data;
    } catch (err) {
      throw err.response?.data?.error || 'Erreur lors du vote';
    }
  }, [gameId]);

  const submitTurn = useCallback(async (actions) => {
    try {
      const res = await axios.post('/api/multi/submit_turn', {
        game_id: parseInt(gameId),
        actions,
      });
      setMultiState(prev => prev ? { ...prev, your_submitted: true } : prev);
      return res.data;
    } catch (err) {
      throw err.response?.data?.error || 'Erreur lors de la soumission';
    }
  }, [gameId]);

  return {
    gameState, multiState, gridData, loading, turnResolved,
    fetchGameState, submitTurn, voteEnd, setGameState,
  };
};

export default useMultiGame;
